import React, { useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import FormField from '../rejestracja/FormField';
import QuestionLabel from '../rejestracja/QuestionLabel';
import { COLORS } from '../rejestracja/formTheme';
import PageDialog from '../../utils/PageDialog';
import { sendContact } from '../../utils/api';
import { formatTranslationNode } from '../../utils/utils';

const schema = z.object({
  name: z.string().trim().min(2, 'err_required'),
  email: z.email('err_email'),
  message: z.string().trim().min(2, 'err_required'),
});

const GeneralForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams();
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: { name: '', email: '', message: '' },
  });

  const onSubmit = async (values) => {
    setError('');
    setSending(true);
    try {
      await sendContact('general', values);
      navigate(`/${lang}/contact/success`);
    } catch (err) {
      console.error(err.message);
      setError(t('submit_error'));
    } finally {
      setSending(false);
    }
  };

  const fields = [
    { name: 'name', type: 'text', label: t('full_name') },
    { name: 'email', type: 'email', label: t('email') },
    { name: 'message', type: 'textarea', label: t('message') },
  ];

  return (
    <PageDialog
      title={t('contact_title')}
      maxWidth={760}
      sx={{
        paddingTop: '10px',
      }}
      actions={
        <Button
          variant='contained'
          type='submit'
          form='contact-form'
          disabled={sending}
          sx={{
            fontWeight: 700,
            minHeight: 44,
            minWidth: 140,
            backgroundColor: COLORS.accent,
            color: COLORS.onAccent,
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.85)' },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(255,255,255,0.5)',
              color: 'rgba(18,18,18,0.6)',
            },
          }}
        >
          {sending ? t('processing') : t('contact_send')}
        </Button>
      }
    >
      <Typography sx={{ color: COLORS.muted, mb: 3 }}>
        {formatTranslationNode('faq-desc', [
          <Link
            key='faq'
            to={`/${lang}/terms/faqs`}
            state={{ previousLocation: location }}
            style={{ color: COLORS.accent, textDecoration: 'underline' }}
          >
            FAQ
          </Link>,
        ])}
      </Typography>

      <Box
        component='form'
        id='contact-form'
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack spacing={3}>
          {fields.map((field) => (
            <Box key={field.name}>
              <QuestionLabel
                component='label'
                sx={{ mb: 1, color: 'rgba(255,255,255,0.8)' }}
              >
                {field.label}
              </QuestionLabel>
              <FormField
                {...field}
                label={null}
                ariaLabel={field.label}
                register={register}
                error={errors[field.name]}
                helperText={errors[field.name]?.message}
                fullWidth
              />
            </Box>
          ))}
          {error && (
            <Alert
              severity='error'
              variant='outlined'
              role='alert'
              sx={{ color: COLORS.error, borderColor: '#ff5f5f' }}
            >
              {error}
            </Alert>
          )}
        </Stack>
      </Box>
    </PageDialog>
  );
};

export default GeneralForm;
